import React, { useState, useEffect } from 'react';
import { useSplash } from '../../../contexts/SplashContext';
import CommonAsyncSelect from '../../Common/CommonAsyncSelect';
import { fetchSettings } from '../../../store/modules/productioncharge/productionchargeActions';
import CommonDate from '../../Common/CommonDate';
import CommonSelect from '../../Common/CommonSelect';
import CommonInput from '../../Common/CommonInput';

import useFormSettings from '../../../hooks/useFormSettings';
import itemService from '../../../services/itemService';
import { useHasPermission } from '../../../utils/permissionUtils';


const ProductionChargeForm = ({ initialData, onSave, isLoading, onClose }) => {
    const hasCreatePermission = useHasPermission(['create production charges']);
    const hasUpdatePermission = useHasPermission(['update production charges']);
    
    const { settings, isLoadingSettings, isSettingsInitialized } = useFormSettings('productioncharge', fetchSettings);
    const { charge_types } = settings || {};
    const splashData = useSplash();
    const languages = splashData?.languages || [];
    const defaultLanguage = languages.find(lang => lang.default)?.code || languages[0]?.code || 'en';
    const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);

    const [formData, setFormData] = useState({
        id: '',
        item: null,
        effective_date: new Date().toISOString().split('T')[0],
        charge_type: { id: 'fixed', label: 'Fixed'},
        amount: 0,
    });
    
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                id: initialData.id || '',
                item: initialData.item || null,
                effective_date: initialData?.effective_date || new Date().toISOString().split('T')[0],
                charge_type: initialData.charge_type || { id: 'fixed', label: 'Fixed'},
                amount: initialData.amount || 0,
            });
        }
    }, [initialData]);

    const handleSelectChange = (name, value) => {
        if(name === 'item') {
            setFormData(prev => {
                return {
                    ...prev,
                    item: value
                }
            })
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    }

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSaving(true);
            await onSave(formData);
            setErrors({});
            onClose();
        } catch (error) {
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(key => {
                    newErrors[key] = Array.isArray(error.errors[key]) 
                        ? error.errors[key][0] 
                        : error.errors[key];
                });
                setErrors(newErrors);
            } else {
                setErrors({
                    name: error.message || 'An error occurred while saving'
                });
            }
            return;
        } finally {
            setIsSaving(false);
        }
    };

    const isCreateMode = !formData?.id;
    const hasPermission = isCreateMode ? hasCreatePermission : hasUpdatePermission;

    const isSubmitting = isLoading || isSaving || !hasPermission;
    const selectedLang = languages.find(l => l.code === selectedLanguage);

    if (isLoadingSettings || !isSettingsInitialized) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-end mb-3">
   
            </div>
            <div className="flex-grow-1">
                <div className="row">
                    <div className="col-md-12">
                        <CommonAsyncSelect
                            name="item"
                            label="Item"
                            value={formData.item}
                            onChange={(value) => handleSelectChange('item', value)}
                            loadOptions={async (inputValue) => {
                                
                                const condition = {
                                    rules: {
                                        condition: 'and',
                                        children: [
                                            {
                                                type: 'rule',
                                                query: {
                                                    operator: 'equal',
                                                    rule: 'items.item_type',
                                                    type: 'text',
                                                    value: 'finished_goods'
                                                }
                                            }
                                        ]
                                    }
                                }

                                const response = await itemService.getList({ search: inputValue }, condition);
                                return response.data.map(item => ({
                                    id: item.id,
                                    label: item.name?.en+' ('+item.item_code+')',
                                }));
                            }}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                    <div className="col-md-12">
                        <CommonDate
                            name="effective_date"
                            label="Effective Date"
                            value={formData.effective_date}
                            onChange={handleChange}
                            errors={errors}
                            required={true}
                            disabled={isSubmitting}
                        />
                    </div>
                    <div className="col-md-12">
                        <CommonSelect
                            name="charge_type"
                            label="Charge Type"
                            value={formData.charge_type}
                            onChange={(value) => handleSelectChange('charge_type', value)}
                            errors={errors}
                            required={true}
                            options={charge_types}
                            isSubmitting={isSubmitting}
                        />
                    </div>
                    <div className="col-md-12">
                        <CommonInput
                            name="amount"
                            label={`Amount ${formData.charge_type?.id === 'percentage' ? '( Percentage )' : ''}`}
                            value={formData.amount}
                            onChange={handleChange}
                            errors={errors}
                            required={true}
                            disabled={isSubmitting}
                        />
                    </div>
                </div>
            </div>

            {hasPermission && (
            <div className="form-footer">
                <button 
                    type="button" 
                    className="btn btn-light" 
                    onClick={onClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </button>
                <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                        </>
                    ) : 'Save'}
                </button>
            </div>
            )}
        </form>
    );
};

export default ProductionChargeForm; 